import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/lib/supabase";
import { glassCardClass, glassMutedCardClass } from "@/recruitment/styles.js";

const todayISO = new Date().toISOString().slice(0, 10);

export default function ParentApplicationPage() {
  const [searchParams] = useSearchParams();
  const campaignSlugFromUrl = useMemo(() => searchParams.get("campaign") ?? "", [searchParams]);
  const branchIdFromUrl = useMemo(() => searchParams.get("branch") ?? "", [searchParams]);
  const authModeFromUrl = useMemo(() => (searchParams.get("mode") === "sign-up" ? "sign-up" : "sign-in"), [searchParams]);

  const [authReady, setAuthReady] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [authForm, setAuthForm] = useState({
    mode: authModeFromUrl,
    email: "",
    password: "",
    first_name: "",
    last_name: "",
  });

  const [contextLoading, setContextLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [selectedCampaignSlug, setSelectedCampaignSlug] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    parent_first_name: "",
    parent_last_name: "",
    parent_email: "",
    parent_phone_e164: "+48",
    child_first_name: "",
    child_last_name: "",
    child_date_of_birth: "",
    child_address: "",
    child_additional_info: "",
  });

  const selectedCampaign = useMemo(
    () => campaigns.find((campaign) => campaign.public_slug === selectedCampaignSlug) || null,
    [campaigns, selectedCampaignSlug]
  );

  const campaignsForSelectedBranch = useMemo(
    () => campaigns.filter((campaign) => campaign.branch_id === selectedBranchId),
    [campaigns, selectedBranchId]
  );

  const onFormChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onAuthFormChange = (key, value) => {
    setAuthForm((prev) => ({ ...prev, [key]: value }));
  };

  const getSessionFromHash = () => {
    const rawHash = window.location.hash || "";
    if (!rawHash.startsWith("#")) return null;

    const params = new URLSearchParams(rawHash.slice(1));
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");
    if (!accessToken || !refreshToken) return null;

    return {
      access_token: accessToken,
      refresh_token: refreshToken,
    };
  };

  const clearHash = () => {
    const cleanUrl = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState({}, document.title, cleanUrl);
  };

  const loadContext = async () => {
    setContextLoading(true);
    try {
      const { data: campaignRows, error: campaignsError } = await supabase
        .from("recruitment_campaigns")
        .select("id, branch_id, campaign_name, public_slug, status, intake_start_at, intake_end_at, decision_publish_at, settings")
        .eq("status", "intake_open")
        .order("intake_end_at", { ascending: true });

      if (campaignsError) throw campaignsError;

      const availableCampaigns = campaignRows ?? [];
      setCampaigns(availableCampaigns);

      const branchIds = [...new Set(availableCampaigns.map((campaign) => campaign.branch_id).filter(Boolean))];
      if (branchIds.length === 0) {
        setBranches([]);
        setSelectedBranchId("");
        setSelectedCampaignSlug("");
        return;
      }

      const { data: branchRows, error: branchesError } = await supabase
        .from("branches")
        .select("id, name")
        .in("id", branchIds)
        .order("name", { ascending: true });
      if (branchesError) throw branchesError;
      setBranches(branchRows ?? []);

      let resolvedBranchId = "";
      let resolvedCampaignSlug = "";

      if (campaignSlugFromUrl) {
        const campaignFromUrl = availableCampaigns.find((campaign) => campaign.public_slug === campaignSlugFromUrl);
        if (campaignFromUrl) {
          if (!branchIdFromUrl || branchIdFromUrl === campaignFromUrl.branch_id) {
            resolvedBranchId = campaignFromUrl.branch_id;
            resolvedCampaignSlug = campaignFromUrl.public_slug;
          } else {
            toast.error("Link kampanii nie pasuje do wskazanej placówki.");
          }
        } else {
          toast.error("Kampania z linku nie jest aktywna.");
        }
      }

      if (!resolvedBranchId && branchIdFromUrl && branchIds.includes(branchIdFromUrl)) {
        resolvedBranchId = branchIdFromUrl;
      }

      if (!resolvedBranchId) {
        resolvedBranchId = branchIds[0];
      }

      if (!resolvedCampaignSlug) {
        const firstCampaignForBranch = availableCampaigns.find((campaign) => campaign.branch_id === resolvedBranchId);
        resolvedCampaignSlug = firstCampaignForBranch?.public_slug || "";
      }

      setSelectedBranchId(resolvedBranchId);
      setSelectedCampaignSlug(resolvedCampaignSlug);
    } catch (err) {
      toast.error(err.message || "Nie udało się załadować aktywnych kampanii.");
    } finally {
      setContextLoading(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      let resolvedUser = null;

      const {
        data: { user: currentUser },
        error: authError,
      } = await supabase.auth.getUser();

      if (!authError) {
        resolvedUser = currentUser ?? null;
      }

      if (!resolvedUser) {
        const hashSession = getSessionFromHash();
        if (hashSession) {
          const { error: setSessionError } = await supabase.auth.setSession(hashSession);
          if (!setSessionError) {
            clearHash();
            const {
              data: { user: userAfterSessionSet },
            } = await supabase.auth.getUser();
            resolvedUser = userAfterSessionSet ?? null;
          }
        }
      }

      setUser(resolvedUser);
      setAuthReady(true);
    };

    bootstrap();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    setAuthForm((prev) => ({ ...prev, mode: authModeFromUrl }));
  }, [authModeFromUrl]);

  useEffect(() => {
    if (!user) return;
    setForm((prev) => ({
      ...prev,
      parent_email: user.email || prev.parent_email,
      parent_first_name: user.user_metadata?.first_name || prev.parent_first_name,
      parent_last_name: user.user_metadata?.last_name || prev.parent_last_name,
    }));
    loadContext();
  }, [user, campaignSlugFromUrl, branchIdFromUrl]);

  const signIn = async (event) => {
    event.preventDefault();
    setAuthLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: authForm.email,
        password: authForm.password,
      });
      if (error) throw error;
      toast.success("Zalogowano.");
    } catch (err) {
      toast.error(err.message || "Nie udało się zalogować.");
    } finally {
      setAuthLoading(false);
    }
  };

  const signUp = async (event) => {
    event.preventDefault();
    setAuthLoading(true);
    try {
      const { error: signUpError } = await supabase.auth.signUp({
        email: authForm.email,
        password: authForm.password,
        options: {
          data: {
            first_name: authForm.first_name,
            last_name: authForm.last_name,
            recruitment_parent: true,
          },
        },
      });
      if (signUpError) throw signUpError;

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: authForm.email,
        password: authForm.password,
      });
      if (signInError) {
        toast.success("Konto utworzone. Zaloguj się, aby kontynuować.");
        setAuthForm((prev) => ({ ...prev, mode: "sign-in" }));
        return;
      }

      toast.success("Konto utworzone i zalogowano.");
    } catch (err) {
      toast.error(err.message || "Nie udało się utworzyć konta.");
    } finally {
      setAuthLoading(false);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setResult(null);
    toast.success("Wylogowano.");
  };

  const submit = async (event) => {
    event.preventDefault();

    if (!selectedCampaignSlug) {
      toast.error("Wybierz aktywną kampanię.");
      return;
    }

    if (!form.child_date_of_birth || form.child_date_of_birth > todayISO) {
      toast.error("Podaj poprawną datę urodzenia dziecka.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        p_campaign_slug: selectedCampaignSlug,
        p_parent_phone_e164: form.parent_phone_e164 || null,
        p_parent_email: form.parent_email || null,
        p_parent_first_name: form.parent_first_name || null,
        p_parent_last_name: form.parent_last_name || null,
        p_child_first_name: form.child_first_name,
        p_child_last_name: form.child_last_name,
        p_child_date_of_birth: form.child_date_of_birth,
        p_child_address: form.child_address || null,
        p_child_snapshot: {
          additionalInfo: form.child_additional_info || null,
        },
        p_scoring_input: {},
      };

      const { data, error } = await supabase.rpc("recruitment_submit_application", payload);
      if (error) throw error;

      const first = Array.isArray(data) ? data[0] : data;
      setResult(first ?? null);
      toast.success("Wniosek został złożony.");
    } catch (err) {
      toast.error(err.message || "Nie udało się złożyć wniosku.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!authReady) {
    return <p className="text-sm text-slate-500">Ładowanie...</p>;
  }

  if (!user) {
    return (
      <div className="space-y-6">
        <Card className={glassCardClass}>
          <CardHeader>
            <CardTitle>Portal rodzica - logowanie</CardTitle>
            <CardDescription>
              Tymczasowo logowanie działa bez OTP (do wdrożenia). Po zalogowaniu wybierzesz placówkę i aktywną kampanię.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant={authForm.mode === "sign-in" ? "default" : "outline"}
                onClick={() => onAuthFormChange("mode", "sign-in")}
              >
                Zaloguj się
              </Button>
              <Button
                type="button"
                variant={authForm.mode === "sign-up" ? "default" : "outline"}
                onClick={() => onAuthFormChange("mode", "sign-up")}
              >
                Utwórz konto
              </Button>
            </div>

            <form onSubmit={authForm.mode === "sign-in" ? signIn : signUp} className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="auth_email">E-mail</Label>
                <Input
                  id="auth_email"
                  type="email"
                  value={authForm.email}
                  onChange={(e) => onAuthFormChange("email", e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="auth_password">Hasło</Label>
                <Input
                  id="auth_password"
                  type="password"
                  value={authForm.password}
                  onChange={(e) => onAuthFormChange("password", e.target.value)}
                  required
                />
              </div>

              {authForm.mode === "sign-up" ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="auth_first_name">Imię</Label>
                    <Input
                      id="auth_first_name"
                      value={authForm.first_name}
                      onChange={(e) => onAuthFormChange("first_name", e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="auth_last_name">Nazwisko</Label>
                    <Input
                      id="auth_last_name"
                      value={authForm.last_name}
                      onChange={(e) => onAuthFormChange("last_name", e.target.value)}
                      required
                    />
                  </div>
                </>
              ) : null}

              <div className="md:col-span-2">
                <Button type="submit" disabled={authLoading}>
                  {authLoading ? "Przetwarzanie..." : authForm.mode === "sign-in" ? "Zaloguj się" : "Utwórz konto"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className={glassCardClass}>
        <CardHeader>
          <CardTitle>Portal rodzica</CardTitle>
          <CardDescription>Zalogowano jako {user.email}. Wybierz placówkę i aktywną kampanię rekrutacyjną.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Placówka</Label>
              <Select value={selectedBranchId} onValueChange={(value) => setSelectedBranchId(value)}>
                <SelectTrigger>
                  <SelectValue placeholder={contextLoading ? "Ładowanie placówek..." : "Wybierz placówkę"} />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Aktywna kampania (intake_open)</Label>
              <Select value={selectedCampaignSlug} onValueChange={(value) => setSelectedCampaignSlug(value)}>
                <SelectTrigger>
                  <SelectValue placeholder={contextLoading ? "Ładowanie kampanii..." : "Wybierz kampanię"} />
                </SelectTrigger>
                <SelectContent>
                  {campaignsForSelectedBranch.map((campaign) => {
                    const schoolYear = campaign?.settings?.schoolYear || "rok nieustawiony";
                    return (
                      <SelectItem key={campaign.id} value={campaign.public_slug}>
                        {campaign.campaign_name} ({schoolYear})
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className={`flex items-center justify-between rounded-md p-3 text-sm ${glassMutedCardClass}`}>
            <div>
              {selectedCampaign ? (
                <p>
                  Wybrana kampania: <span className="font-semibold">{selectedCampaign.campaign_name}</span> (do{" "}
                  {new Date(selectedCampaign.intake_end_at).toLocaleDateString()})
                </p>
              ) : (
                <p>Brak aktywnej kampanii dla wybranej placówki.</p>
              )}
            </div>
            <Button type="button" variant="outline" onClick={signOut}>
              Wyloguj
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className={glassCardClass}>
        <CardHeader>
          <CardTitle>Wniosek rodzica</CardTitle>
          <CardDescription>Wniosek można złożyć tylko do aktywnej kampanii wybranej powyżej.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="parent_first_name">Imię rodzica</Label>
              <Input
                id="parent_first_name"
                value={form.parent_first_name}
                onChange={(e) => onFormChange("parent_first_name", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="parent_last_name">Nazwisko rodzica</Label>
              <Input
                id="parent_last_name"
                value={form.parent_last_name}
                onChange={(e) => onFormChange("parent_last_name", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="parent_email">E-mail</Label>
              <Input id="parent_email" type="email" value={form.parent_email} onChange={(e) => onFormChange("parent_email", e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="parent_phone_e164">Telefon (E.164)</Label>
              <Input
                id="parent_phone_e164"
                value={form.parent_phone_e164}
                onChange={(e) => onFormChange("parent_phone_e164", e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="child_first_name">Imię dziecka</Label>
              <Input id="child_first_name" value={form.child_first_name} onChange={(e) => onFormChange("child_first_name", e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="child_last_name">Nazwisko dziecka</Label>
              <Input id="child_last_name" value={form.child_last_name} onChange={(e) => onFormChange("child_last_name", e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="child_date_of_birth">Data urodzenia</Label>
              <Input
                id="child_date_of_birth"
                type="date"
                max={todayISO}
                value={form.child_date_of_birth}
                onChange={(e) => onFormChange("child_date_of_birth", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="child_address">Adres dziecka</Label>
              <Input id="child_address" value={form.child_address} onChange={(e) => onFormChange("child_address", e.target.value)} required />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="child_additional_info">Dodatkowe informacje</Label>
              <Textarea
                id="child_additional_info"
                value={form.child_additional_info}
                onChange={(e) => onFormChange("child_additional_info", e.target.value)}
                placeholder="Np. potrzeby zdrowotne, ważne informacje dla placówki"
              />
            </div>

            <div className="md:col-span-2">
              <Button type="submit" disabled={submitting || !selectedCampaignSlug}>
                {submitting ? "Wysyłanie..." : "Złóż wniosek"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {result ? (
        <Card className={`${glassCardClass} border-emerald-200/70 bg-emerald-50/55`}>
          <CardHeader>
            <CardTitle>Wniosek przyjęty</CardTitle>
            <CardDescription>Zachowaj dane dostępu do dalszych aktualizacji wniosku.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p>
              <span className="font-semibold">application_id:</span> {result.application_id}
            </p>
            <p>
              <span className="font-semibold">access_token:</span> {result.access_token}
            </p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
